import { Currency } from "@/components/currency";
import { useRemovePayment } from "@/hooks/use-remove-payment";
import { useShop } from "@/hooks/use-shop";
import { getPaymentFromSessionStorage } from "@/features/payment/utils.ts";
import { api } from "@/lib/server";
import { recordTrackedEvent } from "@/lib/kiosk-session-tracking";
import { type PayoutStatus } from "@/features/payment/cash-payment-status";
import { useMutation } from "@tanstack/react-query";
import { Banknote, Clock, Coins } from "lucide-react";
import { Trans } from "react-i18next";

export function useAbortCashPayment() {
  const { data: shop } = useShop();
  const removePayment = useRemovePayment();
  return useMutation({
    mutationFn: async () => {
      recordTrackedEvent({
        eventType: "cash-abort",
      });
      const payment = getPaymentFromSessionStorage();
      return api.abortCashPayment(
        shop.shopId,
        payment?.kioskPaymentNonceKey ?? undefined,
      );
    },
    onSettled: () => {
      const payment = getPaymentFromSessionStorage();
      if (payment) {
        removePayment.mutate({
          key: payment.kioskPaymentNonceKey,
          cause: "cancelled-by-user",
        });
      }
    },
  });
}

export function ChangeReturnDisplay({
  state,
  totalChange,
  dispensed,
}: {
  state: PayoutStatus;
  totalChange: number;
  dispensed: number;
}) {
  if (totalChange === 0) {
    return null;
  }

  return (
    <div className="px-6 pb-8">
      <div className="bg-gradient-to-br from-[#4DB8C4] to-[#3DA8B4] rounded-3xl p-8 mb-6 text-white shadow-lg [&_svg_*]:text-white">
        {state === "PayingOutNote" && (
          <div className="flex flex-col items-center gap-4">
            <div className="relative">
              <Banknote className="w-20 h-20 text-white animate-bounce" />
              <div className="absolute inset-0 bg-white rounded-full opacity-20 animate-ping" />
            </div>
            <p className="text-xl text-white font-bold">
              <Trans>Bankjegy kiadása...</Trans>
            </p>
            <p className="text-sm text-white opacity-90">
              <Trans>Kérjük, várjon</Trans>
            </p>
          </div>
        )}

        {state === "WaitingForNoteRemoval" && (
          <div className="flex flex-col items-center gap-4">
            <div className="relative">
              <Clock className="w-20 h-20 text-white animate-bounce" />
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full animate-bounce" />
            </div>
            <p className="text-xl text-white font-bold">
              <Trans>Vegye el a bankjegyet!</Trans>
            </p>
            <p className="text-sm text-white opacity-90">
              <Trans>
                A folytatáshoz vegye el a kiadott bankjegyet az automatából
              </Trans>
            </p>
          </div>
        )}

        {state === "PayingOutCoin" && (
          <div className="flex flex-col items-center gap-4">
            <div className="relative">
              <Coins className="w-20 h-20 text-white animate-bounce" />
              <div className="absolute inset-0 bg-white rounded-full opacity-20 animate-ping" />
            </div>
            <p className="text-xl text-white font-bold">
              <Trans>Érmék kiadása...</Trans>
            </p>
            <p className="text-sm text-white opacity-90">
              <Trans>Kérjük, várjon</Trans>
            </p>
          </div>
        )}
      </div>

      <div className="bg-gray-50 rounded-2xl p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <div className="text-center flex-1">
            <p className="text-sm text-gray-600 mb-1">
              <Trans>Kiadott összeg</Trans>
            </p>
            <p className="text-3xl font-bold text-[#4DB8C4]">
              <Currency amount={dispensed} />
            </p>
          </div>
          <div className="w-px h-12 bg-gray-300" />
          <div className="text-center flex-1">
            <p className="text-sm text-gray-600 mb-1">
              <Trans>Hátralevő</Trans>
            </p>
            <p className="text-3xl font-bold text-[#2B5F7F]">
              <Currency amount={totalChange - dispensed} />
            </p>
          </div>
        </div>

        <div className="mt-4">
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div
              className="bg-gradient-to-r from-[#4DB8C4] to-[#3DA8B4] h-full rounded-full transition-all duration-500"
              style={{ width: `${(dispensed / totalChange) * 100}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 text-center mt-2">
            {Math.round((dispensed / totalChange) * 100)}% <Trans>kiadva</Trans>
          </p>
        </div>
      </div>
    </div>
  );
}
